import { MoreVertical, Check, Grid3X3 } from 'lucide-react';
import RollingNumber from '../game/RollingNumber';

/**
 * Contest status enum
 */
export type ContestStatus = 'upcoming' | 'live' | 'completed' | 'cancelled';

/**
 * Live game data interface
 */
export interface LiveGameData {
  /** Home/Team 1 name (e.g., "Duke", "Chiefs") */
  team1Name: string;
  /** Home/Team 1 abbreviation (e.g., "DUK", "KC") */
  team1Abbreviation?: string;
  /** Home/Team 1 current score */
  team1Score: number;
  /** Away/Team 2 name (e.g., "Ariz", "49ers") */
  team2Name: string;
  /** Away/Team 2 abbreviation (e.g., "ARIZ", "SF") */
  team2Abbreviation?: string;
  /** Away/Team 2 current score */
  team2Score: number;
  /** Current game period (e.g., "Q1", "Q2", "Halftime", "Q3", "Q4", "Final") */
  period?: string;
  /** Time remaining in period (e.g., "12:45", "00:00") */
  timeRemaining?: string;
  /** User's total winnings for this contest */
  totalWinnings: string;
}

/**
 * Ad slot data for in-card advertising
 */
export interface ContestAdSlot {
  /** Show the ad slot in this contest card */
  enabled: boolean;
  /** Ad image URL from ad service */
  imageUrl?: string;
  /** Click URL for the ad */
  clickUrl?: string;
  /** Ad slot identifier */
  slotId?: string;
}

export interface ContestCardProps {
  /** Unique contest identifier (for API calls) */
  id?: string;
  /** Contest title (e.g., "NFL Squares Sun Oct 9") */
  title: string;
  /** Start time as countdown string (e.g., "05:46:02") or date string (e.g., "1-Day") */
  startTime: string;
  /** Actual start date/time (ISO string for calculations) */
  startDateTime?: string;
  /** Number of games in the contest */
  gameCount: number;
  /** Total number of entries/squares available */
  entries: number;
  /** Number of entries/squares taken */
  entriesTaken?: number;
  /** Entry price (e.g., "FREE" or "$10") */
  entryPrice: string;
  /** Entry price in cents (for calculations) */
  entryPriceCents?: number;
  /** Prize pool amount (e.g., "$500") */
  prizePool: string;
  /** Prize pool in cents (for calculations) */
  prizePoolCents?: number;
  /** League/sport type for logo */
  league?: 'NFL' | 'NBA' | 'NHL' | 'MLB';
  /** Sport code from backend (e.g., 'NFL', 'NBA', 'NHL', 'NCAAMB') - used for logo selection */
  sport?: string;
  /** Logo URL from logo service (optional) */
  logoUrl?: string;
  /** Contest status */
  status?: ContestStatus;
  /** Is this contest live? (convenience prop, derived from status) */
  isLive?: boolean;
  /** Live game data (only used when isLive/status is 'live') */
  liveData?: LiveGameData;
  /** Number of squares the user owns in this contest */
  userSquaresCount?: number;
  /** Ad slot configuration (admin-controlled per contest) */
  adSlot?: ContestAdSlot;
  /** Click handler for the card */
  onClick?: () => void;
  /** Click handler for the menu button */
  onMenuClick?: () => void;
  /** Click handler for view results button (live/completed games) */
  onViewResults?: () => void;
  /** Click handler for view game button (shows the squares grid) */
  onViewGame?: () => void;
  /** Click handler for Edit Pick button (when user has already entered this contest) */
  onEditPick?: () => void;
  /**
   * Click handler for the "Reserve Seat" button — shown for PENDING contests that
   * haven't opened for selections yet and that the user hasn't reserved/entered
   * (DKPLAT-529). Ignored if onEditPick is also provided (already entered wins).
   */
  onReserve?: () => void;
  /** Disables the Reserve Seat button while the reservation request is in flight. */
  isReserving?: boolean;
  /**
   * Whether the current user has reserved a seat for this contest (DKPLAT-529).
   * Combined with hasSelections=false, renders the disabled "Edit"/"Max Entered"
   * reserved-state row (mirrors Pick'em's GameCardFullWidth reserved row) instead
   * of the normal action buttons. Ignored once hasSelections is true — picks
   * submitted takes over the usual Edit Pick flow.
   */
  hasReservation?: boolean;
  /** Whether the user has submitted real picks (as opposed to just a reservation). */
  hasSelections?: boolean;
  /**
   * Whether the squares grid is now open for picks (DKPLAT-529 My Games parity).
   * The Squares equivalent of Pick'em's hasQuestions — the live "squares now
   * available" signal derived server-side, NOT the display-only questionsPostedAt
   * timestamp. With hasReservation && !hasSelections it selects between the
   * reserved states: false → "Entry Reserved" (Edit disabled); true → "No Picks
   * Submitted - Make Selections Now!" (Edit enabled, drives the picks flow).
   */
  hasQuestions?: boolean;
  /** ISO timestamp for when the contest opens for selections, or null. Display-only. */
  questionsPostedAt?: string | null;
  /** Click handler for ad slot */
  onAdClick?: () => void;
  /** Is this game completed/final? (shows FINAL status and completed state) */
  isGameFinal?: boolean;
  /** Does the user have winnings? (affects winnings display color) */
  hasWinnings?: boolean;
}

/**
 * Contest Card Component
 * Displays a contest tile with all relevant info
 * Supports multiple states: upcoming, live, completed
 */
export default function ContestCard({
  id,
  title,
  startTime,
  startDateTime: _startDateTime, // Reserved for future use (countdown calculations)
  gameCount,
  entries,
  entriesTaken,
  entryPrice,
  entryPriceCents: _entryPriceCents, // Reserved for future use (price calculations)
  prizePool,
  prizePoolCents: _prizePoolCents, // Reserved for future use (prize calculations)
  league = 'NFL',
  sport,
  logoUrl,
  status,
  isLive = false,
  liveData,
  userSquaresCount: _userSquaresCount, // Reserved for future use (user's squares badge)
  adSlot,
  onClick,
  onMenuClick,
  onViewResults,
  onViewGame,
  onEditPick,
  onReserve,
  isReserving = false,
  hasReservation = false,
  hasSelections = false,
  hasQuestions = true,
  questionsPostedAt: _questionsPostedAt = null, // Display-only; DK copy is now static text (DKPLAT-533)
  onAdClick,
  isGameFinal = false,
  hasWinnings = false,
}: ContestCardProps) {
  // Determine if contest is live (from status or isLive prop)
  const contestIsLive = status === 'live' || isLive;
  const contestIsCompleted = status === 'completed' || isGameFinal;

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMenuClick?.();
  };

  const handleViewResults = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewResults?.();
  };

  const handleViewGame = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewGame?.();
  };

  const handleEditPick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditPick?.();
  };

  const handleReserve = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReserve?.();
  };

  const handleAdClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAdClick) {
      onAdClick();
    } else if (adSlot?.clickUrl) {
      window.open(adSlot.clickUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const showActionButtons = (contestIsLive || contestIsCompleted) && (onViewResults || onViewGame);
  // A reservation that never converted to real picks (DKPLAT-529 My Games parity).
  // Drives the three reserved states below, mirroring Pick'em's GameCard.
  const isReservedUnconverted = hasReservation && !hasSelections;
  // Pre-live reserved row (states 1 & 2): shown while the contest is still open.
  //   State 1 (!hasQuestions): "Entry Reserved" + disabled Edit + Max Entered.
  //   State 2 (hasQuestions):  "No Picks Submitted - Make Selections Now!" (emphasised)
  //                            + ENABLED Edit wired to the picks flow + Max Entered.
  // Takes priority over the normal Edit Pick / Reserve Seat buttons below.
  const showReservedRow = !contestIsLive && !contestIsCompleted && isReservedUnconverted;
  // State 3: once the contest locks (live/final) with the reservation still
  // unconverted, the "No Picks Submitted" label persists through live → Past Results.
  const showNoPicksLabel = (contestIsLive || contestIsCompleted) && isReservedUnconverted;
  const showEditPickButton =
    !contestIsLive && !contestIsCompleted && !showReservedRow && !!onEditPick;
  // Reserve takes a back seat to Edit Pick — a user who's already entered never needs it.
  const showReserveButton =
    !contestIsLive && !contestIsCompleted && !showReservedRow && !onEditPick && !!onReserve;
  const showScores = (contestIsLive || contestIsCompleted) && !!liveData;

  return (
    <div
      onClick={onClick}
      data-contest-id={id}
      className={`bg-[#242424] rounded-xl p-4 cursor-pointer hover:bg-[#2a2a2a] transition-colors ${
        contestIsLive ? 'border-2 border-[#53d337]' : 'border border-[#373737]'
      }`}
    >
      {/* Conditional Ad Slot - Above title/logo row (Legacy imageUrl/clickUrl or placeholder) */}
      {adSlot?.enabled && (
        <div
          onClick={adSlot.imageUrl || adSlot.clickUrl ? handleAdClick : undefined}
          className={`w-full h-[50px] mb-4 flex items-center justify-center overflow-hidden ${
            adSlot.imageUrl ? 'cursor-pointer' : ''
          }`}
          style={{ backgroundColor: '#2d4a5e' }}
          role="banner"
          aria-label="Advertisement"
          data-ad-slot={adSlot.slotId || 'contest-banner'}
        >
          {adSlot.imageUrl ? (
            <img src={adSlot.imageUrl} alt="Advertisement" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center">
              <p className="text-zinc-400 text-xs">Web Advert</p>
              <p className="text-zinc-500 text-[10px]">320×50</p>
            </div>
          )}
        </div>
      )}
      {/* Top Row: Logo, Title, Status, Menu */}
      <div className="flex items-start gap-3 mb-4">
        {/* League Logo - populated by logo service */}
        <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-[#373737] flex items-center justify-center overflow-hidden">
          <LeagueLogo league={league} sport={sport} logoUrl={logoUrl} />
        </div>

        {/* Title and Status */}
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-base line-clamp-2">{title}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            {contestIsLive ? (
              <>
                <span className="w-2 h-2 rounded-full bg-[#53d337] animate-pulse" />
                <span className="text-[#53d337] font-semibold text-sm">LIVE</span>
                {(liveData?.period || liveData?.timeRemaining) && (
                  <span className="text-[#53d337] font-mono text-sm font-medium">
                    {[liveData.period, liveData.timeRemaining].filter(Boolean).join(' ')}
                  </span>
                )}
                <span className="text-zinc-500 text-sm">
                  ({gameCount} {gameCount === 1 ? 'Game' : 'Games'})
                </span>
              </>
            ) : contestIsCompleted ? (
              <>
                <span className="text-zinc-400 font-semibold text-sm">Final</span>
                <span className="text-zinc-500 text-sm">
                  ({gameCount} {gameCount === 1 ? 'Game' : 'Games'})
                </span>
              </>
            ) : (
              <>
                <span className="text-zinc-500 text-sm">Start:</span>
                <span className="text-[#53d337] font-mono text-sm font-medium">{startTime}</span>
                <span className="text-zinc-500 text-sm">
                  ({gameCount} {gameCount === 1 ? 'Game' : 'Games'})
                </span>
              </>
            )}
          </div>
        </div>

        {/* Menu Button */}
        <button
          onClick={handleMenuClick}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-zinc-700 transition-colors"
          aria-label="More options"
        >
          <MoreVertical className="w-5 h-5 text-zinc-400" />
        </button>
      </div>

      {/* Bottom Section: Stats or Live Scores */}
      {showScores ? (
        <>
          {/* Live/Final Scores Row */}
          <div className="flex items-center mb-4">
            {/* Team 1 */}
            <div className="flex-1">
              <p className="text-zinc-500 text-xs mb-0.5">
                {liveData.team1Abbreviation || liveData.team1Name}
              </p>
              <p className="text-white font-semibold text-lg">
                <RollingNumber value={liveData.team1Score} />
              </p>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-[#373737] mx-4" />

            {/* Team 2 */}
            <div className="flex-1">
              <p className="text-zinc-500 text-xs mb-0.5">
                {liveData.team2Abbreviation || liveData.team2Name}
              </p>
              <p className="text-white font-semibold text-lg">
                <RollingNumber value={liveData.team2Score} />
              </p>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-[#373737] mx-4" />

            {/* Total Winnings */}
            <div className="flex-1 text-right">
              <p className="text-zinc-500 text-xs mb-0.5">Total Winnings</p>
              <p
                className={`font-semibold text-lg ${
                  isGameFinal && (!hasWinnings || liveData.totalWinnings === '$0')
                    ? 'text-zinc-500'
                    : 'text-[#53d337]'
                }`}
              >
                {liveData.totalWinnings}
              </p>
            </div>
          </div>
        </>
      ) : (
        /* Regular Stats Row (Upcoming contests) */
        <div className="flex items-center">
          {/* Entries */}
          <div className="flex-1">
            <p className="text-zinc-500 text-xs mb-0.5">Entries</p>
            <p className="text-white font-semibold">
              {entriesTaken !== undefined
                ? `${entriesTaken.toLocaleString()}/${entries.toLocaleString()}`
                : entries.toLocaleString()}
            </p>
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-[#373737] mx-4" />

          {/* Entry Price */}
          <div className="flex-1">
            <p className="text-zinc-500 text-xs mb-0.5">Entry</p>
            <p className="text-white font-semibold">{entryPrice}</p>
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-[#373737] mx-4" />

          {/* Prize Pool */}
          <div className="flex-1 text-right">
            <p className="text-zinc-500 text-xs mb-0.5">Prize Pool</p>
            <p className="text-[#53d337] font-semibold">{prizePool}</p>
          </div>
        </div>
      )}

      {/* Action Buttons (Live/Completed) */}
      {showActionButtons && (
        <div className="mt-4 flex justify-center gap-2 flex-wrap">
          {onViewResults && (
            <button
              onClick={handleViewResults}
              className="w-[132px] h-[32px] rounded-full border border-[#373737] bg-[#121212] text-white flex items-center justify-center gap-2 transition-colors hover:border-[#53d337]"
              style={{ letterSpacing: '0.5%', fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <span className="text-[13px] font-semibold tracking-[0.5%]">View Results</span>
              <span className="w-[13px] h-[13px] rounded-full bg-[#6F6F6F] flex items-center justify-center">
                <Check className="w-[7px] h-[7px] text-black" strokeWidth={3} />
              </span>
            </button>
          )}
          {onViewGame && (
            <button
              onClick={handleViewGame}
              className="w-[132px] h-[32px] rounded-full border border-[#373737] bg-[#121212] text-white flex items-center justify-center gap-2 transition-colors hover:border-[#53d337]"
              style={{ letterSpacing: '0.5%', fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <span className="text-[13px] font-semibold tracking-[0.5%]">View Game</span>
              <span className="w-[13px] h-[13px] rounded-full bg-[#6F6F6F] flex items-center justify-center">
                <Grid3X3 className="w-[8px] h-[8px] text-black" strokeWidth={3} />
              </span>
            </button>
          )}
        </div>
      )}

      {/* State 3 (DKPLAT-529): reservation never converted to a pick — the
          "No Picks Submitted" label persists as the tile moves through live →
          Past Results, so it no longer vanishes once the contest locks. */}
      {showNoPicksLabel && (
        <p
          data-test-id={`reserved-no-picks-${id}`}
          className="mt-3 text-sm font-semibold text-yellow-500 text-center"
        >
          No Picks Submitted
        </p>
      )}

      {/* Edit Pick button (upcoming, user has already entered) */}
      {showEditPickButton && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleEditPick}
            className="w-[132px] h-[32px] rounded-full border border-[#53d337] bg-[#53d337]/10 text-[#53d337] flex items-center justify-center gap-2 transition-colors hover:bg-[#53d337]/20"
            style={{ letterSpacing: '0.5%', fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            <span className="text-[13px] font-semibold tracking-[0.5%]">Edit Pick</span>
            <span className="w-[13px] h-[13px] rounded-full bg-[#53d337]/30 flex items-center justify-center">
              <Grid3X3 className="w-[8px] h-[8px] text-black" strokeWidth={3} />
            </span>
          </button>
        </div>
      )}

      {/* Reserved-but-not-yet-picked row (DKPLAT-529): mirrors Pick'em's reserved row.
          Two pre-live states driven by hasQuestions (DK copy from DKPLAT-533):
            State 1 (!hasQuestions): "Entry Reserved" + disabled Edit + Max Entered.
            State 2 (hasQuestions):  "No Picks Submitted - Make Selections Now!"
                                     (emphasised) + ENABLED Edit driving the picks flow.
          No Share button (removed by product decision on the Pick'em side — DKPLAT-528). */}
      {showReservedRow && (
        <>
          <p
            data-test-id={
              hasQuestions ? `reserved-make-selections-${id}` : `reserved-entry-reserved-${id}`
            }
            className={
              hasQuestions
                ? 'mt-3 text-base font-bold text-[#53d337] text-center'
                : 'mt-3 text-sm font-semibold text-yellow-500 text-center'
            }
          >
            {hasQuestions ? 'No Picks Submitted - Make Selections Now!' : 'Entry Reserved'}
          </p>
          <div className="mt-4 flex gap-2 justify-between w-full">
            {hasQuestions ? (
              <button
                onClick={handleEditPick}
                data-test-id={`reserved-edit-picks-${id}`}
                className="rounded-full border border-[#53d337] bg-[#53d337]/10 text-[#53d337] whitespace-nowrap px-3 flex-1 py-2 text-[13px] font-semibold transition-colors hover:bg-[#53d337]/20"
              >
                Edit Pick
              </button>
            ) : (
              <button
                disabled
                data-test-id={`reserved-edit-picks-disabled-${id}`}
                className="rounded-full border border-[#373737] bg-[#121212] text-zinc-500 whitespace-nowrap px-3 flex-1 py-2 cursor-not-allowed text-[13px] font-semibold"
              >
                Edit Pick
              </button>
            )}
            <button
              disabled
              data-test-id={`reserved-max-entered-${id}`}
              className="rounded-full bg-[#53d337]/40 text-black/70 whitespace-nowrap px-3 flex-1 py-2 cursor-not-allowed text-[13px] font-semibold"
            >
              Max Entered
            </button>
          </div>
        </>
      )}

      {/* Reserve Seat button (DKPLAT-529 — contest hasn't opened for selections yet) */}
      {showReserveButton && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleReserve}
            disabled={isReserving}
            className="w-[140px] h-[32px] rounded-full bg-[#53d337] text-black flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ letterSpacing: '0.5%', fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            <span className="text-[13px] font-semibold tracking-[0.5%]">Reserve Seat</span>
          </button>
        </div>
      )}
    </div>
  );
}

export interface LeagueLogoProps {
  /** League identifier for logo service */
  league: string;
  /** Optional custom logo URL (from logo service) */
  logoUrl?: string;
  /** Sport code from backend (e.g., 'NFL', 'NBA', 'NHL', 'NCAAMB') */
  sport?: string;
}

/**
 * Get league logo path based on sport
 */
const getLeagueLogoPath = (sport?: string, league?: string): string | null => {
  // Use sport if available, otherwise fall back to league
  const identifier = sport || league;

  if (!identifier) return null;

  const sportUpper = identifier.toUpperCase();

  // Map sport codes to logo paths
  // NCAAFB (college football) rides the NFL badge, matching the lobby's
  // ncaafb→nfl grouping (DKPLAT-673).
  if (sportUpper === 'NFL' || sportUpper === 'NCAAFB') {
    return '/leagues/nfl-logo.svg';
  }
  if (sportUpper === 'NBA') {
    return '/leagues/nba-logo.svg';
  }
  if (sportUpper === 'NHL') {
    return '/leagues/nhl-logo.svg';
  }
  if (sportUpper === 'NCAAMB' || sportUpper === 'NCAA') {
    return '/leagues/Arbysncaa-logo.png';
  }

  return null;
};

/**
 * League Logo Component
 * Displays league logo based on sport/league type
 */
export function LeagueLogo({ league, logoUrl, sport }: LeagueLogoProps) {
  // If logo URL is provided from service, use it
  if (logoUrl) {
    return <img src={logoUrl} alt={`${league} logo`} className="w-full h-full object-contain" />;
  }

  // Get league logo path based on sport/league
  const logoPath = getLeagueLogoPath(sport, league);

  if (logoPath) {
    return (
      <img
        src={logoPath}
        alt={`${sport || league} logo`}
        className="w-full h-full object-contain"
      />
    );
  }

  // Placeholder - fallback if no logo found
  return (
    <div className="w-full h-full flex items-center justify-center bg-[#373737] rounded">
      <span className="text-zinc-400 font-medium text-[10px]">Logo</span>
    </div>
  );
}
